// Compact, single-line newsletter signup designed for the footer legal
// bar. Same backend + UX as `NewsletterSignup` (insert into
// `newsletter_subscribers`, friendly toast on dup) but tighter spacing
// and a smaller success state so the whole bar stays one row on desktop.
import { useState } from "react";
import { Mail, CheckCircle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const InlineNewsletterSignup = () => {
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;

    setLoading(true);
    const { error } = await supabase
      .from("newsletter_subscribers")
      .insert({ email: trimmed.slice(0, 255).toLowerCase() });
    setLoading(false);

    if (error) {
      if (error.code === "23505") {
        toast({ title: "Already subscribed!", description: "This email is already on our list." });
        setSubscribed(true);
      } else {
        toast({ title: "Error", description: "Something went wrong. Please try again.", variant: "destructive" });
      }
      return;
    }

    setSubscribed(true);
    toast({ title: "Subscribed!", description: "Welcome to the HackerLabs newsletter." });
  };

  if (subscribed) {
    return (
      <div className="flex items-center gap-1.5 text-primary font-mono text-[11px]">
        <CheckCircle className="w-3 h-3" />
        <span>Locked in. See you Monday.</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex items-center gap-1.5 w-full">
      <span className="hidden sm:inline text-muted-foreground/70 whitespace-nowrap">
        Weekly drops →
      </span>
      <div className="relative flex-1 min-w-0">
        <Mail className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          maxLength={255}
          required
          aria-label="Email address for newsletter"
          className="w-full pl-6 pr-2 py-1.5 rounded-md bg-secondary border border-border text-foreground placeholder:text-muted-foreground/60 font-mono text-[11px] focus:outline-none focus:ring-1 focus:ring-primary/50"
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        className="px-2.5 py-1.5 rounded-md bg-primary text-primary-foreground font-mono text-[11px] font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 inline-flex items-center gap-1"
      >
        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Join"}
      </button>
    </form>
  );
};

export default InlineNewsletterSignup;